import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import PhoneFrame from "@/components/PhoneFrame";
import { toast } from "sonner";
import { Home, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export default function JoinHousehold() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/households/join/${token}/info`); setInfo(data); }
      catch (e) { setError(e?.response?.data?.detail || "Invite invalid"); }
    })();
  }, [token]);

  const accept = async () => {
    if (!user) { nav(`/auth?mode=login&next=${encodeURIComponent(`/household/join/${token}`)}`); return; }
    setBusy(true);
    try { const { data } = await api.post(`/households/join/${token}`); toast.success("Joined household"); nav("/app/household"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (error) return (
    <PhoneFrame><div className="px-6 pt-16 text-center">
      <AlertCircle className="h-12 w-12 mx-auto text-destructive" />
      <div className="font-display text-xl mt-4">Invite unavailable</div>
      <div className="text-sm text-muted-foreground mt-1">{error}</div>
    </div></PhoneFrame>
  );

  if (!info) return <PhoneFrame><div className="px-6 pt-16 text-center text-muted-foreground">Loading…</div></PhoneFrame>;

  return (
    <PhoneFrame>
      <div className="p-6 space-y-4">
        <div className="h-12 w-12 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center"><Home className="h-6 w-6" /></div>
        <div>
          <div className="font-display text-2xl font-semibold">Join {info.household.name}</div>
          <div className="text-sm text-muted-foreground mt-1">Invited by {info.household.owner?.name || info.household.owner?.email} • {info.household.members_count} member{info.household.members_count !== 1 ? "s" : ""}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="text-sm">You'll be able to see items shared with this household — notes, documents, photos and (encrypted) passwords members choose to share.</p>
        </div>
        <Button className="w-full h-11" onClick={accept} disabled={busy || loading} data-testid="household-join-accept">Join household</Button>
        {!user && <div className="text-xs text-center text-muted-foreground">You'll be asked to sign in first.</div>}
      </div>
    </PhoneFrame>
  );
}
